import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTrack, getQueue, listTracks, patchTrack, resolve, verifyTrack,
  type QueueItem, type TrackFields, type TrackRow,
} from '../api/tracks'
import { describeError } from '../lib/describeError'
import { canPublish, useEditorialMe } from './me'
import { useEditorialTitle } from './titleContext'
import { KIND_LABEL, actionsFor, queueBadge, safeWebsite, type QueueAction } from './trackFinder'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const field = `min-h-10 w-full rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 ${focus}`
const chip = 'inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-semibold text-paper-dim'
const chipOverdue = 'inline-flex h-6 items-center rounded-full bg-amber-glow px-2.5 font-ui text-[12px] font-semibold text-ink-900'

const US_STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ')

export const EMPTY_QUEUE = 'Nothing waiting. Reader reports and new tracks show up here.'

const where = (t: Pick<TrackRow, 'city' | 'state'>) => [t.city, t.state].filter(Boolean).join(', ')

function TrackLine({ t, label }: { t: TrackRow; label?: string }) {
  return (
    <div className="font-ui text-[14px] text-paper">
      {label && <span className="mr-2 text-paper-faint">{label}</span>}
      <span className="font-semibold">{t.name}</span>
      <span className="text-paper-dim"> · {where(t)} · {t.status}</span>
    </div>
  )
}

function Website({ value }: { value: unknown }) {
  const href = safeWebsite(value)
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className="text-amber-glow underline">{href}</a>
  return <>{typeof value === 'string' ? value : ''}</>
}

function PayloadView({ payload }: { payload: Record<string, unknown> }) {
  const rows = ['name', 'city', 'state', 'website'].filter((k) => payload[k] != null && payload[k] !== '')
  const kinds = Array.isArray(payload.kinds) ? payload.kinds.filter((k) => typeof k === 'string').join(', ') : ''
  if (!rows.length && !kinds) return null
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-ui text-[14px]">
      {rows.map((k) => (
        <div key={k} className="contents">
          <dt className="text-paper-faint capitalize">{k}</dt>
          <dd className="text-paper break-words">{k === 'website' ? <Website value={payload[k]} /> : String(payload[k])}</dd>
        </div>
      ))}
      {kinds && (
        <div className="contents"><dt className="text-paper-faint">Kinds</dt><dd className="text-paper">{kinds}</dd></div>
      )}
    </dl>
  )
}

const ACTION_LABEL: Record<QueueAction, string> = {
  approve: 'Approve', verify: 'Mark verified', close: 'Close track', merge: 'Keep this one', dismiss: 'Dismiss',
}

function QueueCard({ item, isEditor, onDone }: { item: QueueItem; isEditor: boolean; onDone: () => void }) {
  const badge = queueBadge(item)
  const act = useMutation({
    mutationFn: (v: { action: QueueAction; merge_into?: number }) => resolve(item.id, v),
    onSuccess: onDone,
  })
  const actions = item.resolved_at ? [] : actionsFor(item.kind, isEditor, item.track != null)
  const run = (action: QueueAction, merge_into?: number) => {
    if (action === 'close' && !window.confirm('Close this track? Readers will stop seeing it.')) return
    act.mutate({ action, merge_into })
  }
  return (
    <li className={`${card} flex flex-col gap-3 px-[18px] py-4`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-ui text-[15px] font-semibold text-paper">{KIND_LABEL[item.kind] ?? item.kind}</span>
        <span className={badge.tone === 'overdue' ? chipOverdue : chip}>{badge.label}</span>
        {item.resolved_at && <span className={chip}>Resolved: {item.resolution}</span>}
        {item.reason && <span className={chip}>{item.reason}</span>}
      </div>
      {item.track && <TrackLine t={item.track} />}
      {item.other_track && <TrackLine t={item.other_track} label="and" />}
      {item.note && <p className="whitespace-pre-wrap font-ui text-[14px] text-paper-dim">{item.note}</p>}
      {item.payload && <PayloadView payload={item.payload} />}
      {item.reader_email && <div className="font-ui text-[13px] text-paper-faint">From {item.reader_email}</div>}
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((a) =>
            a === 'merge' && item.track && item.other_track ? (
              <span key={a} className="flex flex-wrap gap-2">
                <button className={btn} disabled={act.isPending} onClick={() => run('merge', item.track!.id)}>Keep “{item.track.name}” (#{item.track.id})</button>
                <button className={btn} disabled={act.isPending} onClick={() => run('merge', item.other_track!.id)}>Keep “{item.other_track.name}” (#{item.other_track.id})</button>
              </span>
            ) : (
              <button key={a} className={a === 'approve' || a === 'verify' ? btnPrimary : btn} disabled={act.isPending} onClick={() => run(a)}>
                {ACTION_LABEL[a]}
              </button>
            ))}
        </div>
      )}
      {act.isError && <p role="alert" className="font-ui text-[13px] text-amber-glow">{describeError(act.error)}</p>}
    </li>
  )
}

function QueueView({ isEditor }: { isEditor: boolean }) {
  const qc = useQueryClient()
  const [resolved, setResolved] = useState(false)
  const q = useQuery({ queryKey: ['tracks', 'queue', resolved], queryFn: () => getQueue(resolved) })
  const done = () => { void qc.invalidateQueries({ queryKey: ['tracks'] }); void qc.invalidateQueries({ queryKey: ['navrail-dashboard-counts'] }) }
  if (q.isPending) return <p className="font-ui text-paper-dim">Loading…</p>
  if (q.isError) return <p role="alert" className="font-ui text-amber-glow">{describeError(q.error)}</p>
  const { items, open, overdue } = q.data
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 font-ui text-[14px] text-paper-dim">
        <span>{open} waiting</span>
        {overdue > 0 && <span className={chipOverdue}>Overdue: {overdue}</span>}
        <label className="ml-auto flex items-center gap-2">
          <input type="checkbox" checked={resolved} onChange={(e) => setResolved(e.target.checked)} /> Show resolved
        </label>
      </div>
      {items.length === 0 ? (
        <p className="font-ui text-paper-dim">{EMPTY_QUEUE}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((it) => <QueueCard key={it.id} item={it} isEditor={isEditor} onDone={done} />)}
        </ul>
      )}
    </div>
  )
}

type Form = Record<'name' | 'address' | 'city' | 'state' | 'zip' | 'website' | 'social_url' | 'phone' | 'types' | 'surfaces' | 'setting' | 'status', string>
const TEXT_KEYS = ['name', 'address', 'city', 'state', 'zip', 'website', 'social_url', 'phone'] as const

const toForm = (t?: TrackRow): Form => ({
  name: t?.name ?? '', address: t?.address ?? '', city: t?.city ?? '', state: t?.state ?? '', zip: t?.zip ?? '',
  website: t?.website ?? '', social_url: t?.social_url ?? '', phone: t?.phone ?? '',
  types: (t?.types ?? []).join(', '), surfaces: (t?.surfaces ?? []).join(', '), setting: t?.setting ?? '', status: t?.status ?? 'listed',
})
const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

function TrackEditor({ track, isEditor, onSaved, onCancel }: { track?: TrackRow; isEditor: boolean; onSaved: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Form>(toForm(track))
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const body = (): TrackFields => {
    const b: Record<string, unknown> = { types: list(f.types), surfaces: list(f.surfaces), setting: f.setting || null }
    for (const k of TEXT_KEYS) b[k] = f[k].trim() === '' ? null : f[k].trim()
    b.name = f.name.trim()
    b.state = f.state.trim()
    // Writers may not send status at all (the server refuses the key).
    if (isEditor && track) b.status = f.status
    return b as TrackFields
  }
  const save = useMutation({
    mutationFn: () => (track ? patchTrack(track.id, body()) : createTrack(body() as TrackFields & { name: string; state: string })),
    onSuccess: onSaved,
  })
  const verify = useMutation({ mutationFn: () => verifyTrack(track!.id, body()), onSuccess: onSaved })
  const err = save.isError ? save.error : verify.isError ? verify.error : null
  return (
    <div className={`${card} flex flex-col gap-3 p-4`}>
      <div className="grid gap-3 sm:grid-cols-2">
        {TEXT_KEYS.map((k) => (
          <label key={k} className="flex flex-col gap-1 font-ui text-[13px] text-paper-faint">
            {k === 'social_url' ? 'social url' : k}
            <input className={field} value={f[k]} onChange={set(k)} />
          </label>
        ))}
        <label className="flex flex-col gap-1 font-ui text-[13px] text-paper-faint">types (comma separated)
          <input className={field} value={f.types} onChange={set('types')} /></label>
        <label className="flex flex-col gap-1 font-ui text-[13px] text-paper-faint">surfaces (comma separated)
          <input className={field} value={f.surfaces} onChange={set('surfaces')} /></label>
        <label className="flex flex-col gap-1 font-ui text-[13px] text-paper-faint">setting
          <select className={field} value={f.setting} onChange={set('setting')}>
            <option value="">Not set</option><option value="indoor">Indoor</option><option value="outdoor">Outdoor</option><option value="both">Both</option>
          </select></label>
        {track && isEditor && (
          <label className="flex flex-col gap-1 font-ui text-[13px] text-paper-faint">status
            <select className={field} value={f.status} onChange={set('status')}>
              <option value="listed">Listed</option><option value="closed">Closed</option><option value="hidden">Hidden</option>
            </select></label>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button className={btnPrimary} disabled={save.isPending} onClick={() => save.mutate()}>{track ? 'Save' : 'Add track'}</button>
        {track && isEditor && <button className={btn} disabled={verify.isPending} onClick={() => verify.mutate()}>Save and mark verified</button>}
        <button className={btn} onClick={onCancel}>Cancel</button>
      </div>
      {err != null && <p role="alert" className="font-ui text-[13px] text-amber-glow">{describeError(err)}</p>}
    </div>
  )
}

function TracksView({ isEditor }: { isEditor: boolean }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [state, setState] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const res = useQuery({ queryKey: ['tracks', 'list', q, state, status, page], queryFn: () => listTracks({ q, state, status, page }), placeholderData: (p) => p })
  const saved = () => { setEditing(null); void qc.invalidateQueries({ queryKey: ['tracks'] }) }
  const data = res.data
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <input className={`${field} max-w-xs`} placeholder="Search name or city" value={q} onChange={(e) => { setQ(e.target.value); setPage(1) }} />
        <select className={`${field} max-w-[9rem]`} value={state} onChange={(e) => { setState(e.target.value); setPage(1) }} aria-label="State">
          <option value="">Any state</option>
          {US_STATES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={`${field} max-w-[10rem]`} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }} aria-label="Status">
          <option value="">Any status</option><option value="listed">Listed</option><option value="closed">Closed</option><option value="hidden">Hidden</option>
        </select>
        {isEditor && <button className={`${btnPrimary} ml-auto`} onClick={() => setEditing('new')}>Add a track</button>}
      </div>
      {editing === 'new' && <TrackEditor isEditor={isEditor} onSaved={saved} onCancel={() => setEditing(null)} />}
      {res.isError && <p role="alert" className="font-ui text-amber-glow">{describeError(res.error)}</p>}
      {data && (
        <>
          <p className="font-ui text-[13px] text-paper-faint">{data.total} track{data.total === 1 ? '' : 's'}</p>
          <ul className="flex flex-col gap-2">
            {data.items.map((t) => (
              <li key={t.id} className="flex flex-col gap-2">
                <div className={`${card} flex flex-wrap items-center gap-3 px-4 py-3`}>
                  <TrackLine t={t} />
                  {t.verified_at ? <span className={chip}>Verified</span> : <span className={chip}>Unverified</span>}
                  <button className={`${btn} ml-auto`} onClick={() => setEditing(editing === t.id ? null : t.id)}>Edit</button>
                </div>
                {editing === t.id && <TrackEditor track={t} isEditor={isEditor} onSaved={saved} onCancel={() => setEditing(null)} />}
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <div className="flex items-center gap-3 font-ui text-[14px] text-paper-dim">
              <button className={btn} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span>Page {page} of {pages}</span>
              <button className={btn} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** Editorial > Track Finder (RC Car Action only): the review queue and track editing. */
export function TrackFinderPage() {
  const { publicationSlug } = useEditorialTitle()
  const me = useEditorialMe()
  const [view, setView] = useState<'queue' | 'tracks'>('queue')
  if (publicationSlug !== 'rc_car_action') return <Navigate to=".." relative="path" replace />
  const isEditor = canPublish(me.data)
  const tab = (v: 'queue' | 'tracks', label: string) => (
    <button aria-pressed={view === v} onClick={() => setView(v)}
      className={`${focus} rounded-lg px-4 py-2 font-ui text-[14px] font-semibold ${view === v ? 'bg-ink-600 text-paper' : 'text-paper-dim hover:text-paper'}`}>{label}</button>
  )
  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2">{tab('queue', 'Queue')}{tab('tracks', 'Tracks')}</div>
      {view === 'queue' ? <QueueView isEditor={isEditor} /> : <TracksView isEditor={isEditor} />}
    </div>
  )
}
